// src/pages/HelpCenter.jsx

import React from 'react';
import { Container, Typography, Grid, Paper, Box } from '@mui/material';
import { styled } from '@mui/system';
import { Link } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

// --- STYLING (Consistent Hard-Edge, Modern Theme) ---

const Root = styled('div')({
    backgroundColor: '#FFFFFF',
    color: '#111111',
    minHeight: '100vh',
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
});

const StyledContainer = styled(Container)({
    paddingTop: '8rem',
    paddingBottom: '6rem',
});

const HeaderTypography = styled(Typography)({
    fontWeight: 700,
    fontSize: '3rem',
    letterSpacing: '-1px',
    marginBottom: '1rem',
    textAlign: 'center',
});

const SubHeaderTypography = styled(Typography)({
    fontSize: '1.25rem',
    color: '#666666',
    marginBottom: '4rem',
    textAlign: 'center',
    maxWidth: '600px',
    marginLeft: 'auto',
    marginRight: 'auto',
});

const StyledCard = styled(Paper)({
    padding: '2rem',
    height: '100%',
    borderRadius: '8px',
    border: '1px solid #EAEAEA',
    backgroundColor: '#FFFFFF',
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
    // The 'transition' and '&:hover' properties have been removed.
});

const CardLink = styled(Link)({
    textDecoration: 'none',
    color: 'inherit',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
});

const CardTitle = styled(Typography)({
    fontWeight: 600,
    fontSize: '1.25rem',
    marginBottom: '0.75rem',
});

const CardDescription = styled(Typography)({
    color: '#555555',
    flexGrow: 1,
});

const CardAction = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    marginTop: '1.5rem',
    color: '#12c099',
    fontWeight: 600,
});


const HelpCenter = () => {
    const helpTopics = [
        {
            title: "Account Management",
            description: "Learn how to update your profile, change your password, and manage other account settings.",
            link: "/account-management"
        },
        {
            title: "Payments & Billing",
            description: "Get help with updating payment methods, understanding billing cycles, and resolving payment issues.",
            link: "/payment-issues"
        },
        {
            title: "Technical Support",
            description: "Tips for troubleshooting common technical issues and how to contact our team for further assistance.",
            link: "/technical-support"
        },
        {
            title: "Subscription Plans",
            description: "Information about our membership plans, including the benefits and how to switch between them.",
            link: "/subscription-plans"
        },
        {
            title: "Using Credits",
            description: "Learn how to use credits for accessing various activities and events in the Academy.",
            link: "/using-credits"
        },
        {
            title: "Contact Us",
            description: "Find out the best way to contact our support team for any additional assistance.",
            link: "/contact-us"
        }
    ];

    return (
        <Root>
            <StyledContainer maxWidth="lg">
                <HeaderTypography variant="h2">
                    Help Center
                </HeaderTypography>
                <SubHeaderTypography>
                    Find the help you need. Browse topics, find answers to common questions, or get in touch with our support team.
                </SubHeaderTypography>
                <Grid container spacing={3}>
                    {helpTopics.map((topic, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <CardLink to={topic.link}>
                                <StyledCard elevation={0}>
                                    <CardTitle variant="h3">
                                        {topic.title}
                                    </CardTitle>
                                    <CardDescription variant="body1">
                                        {topic.description}
                                    </CardDescription>
                                    <CardAction>
                                        <Typography sx={{ fontWeight: 600 }}>Learn more</Typography>
                                        <ArrowForwardIcon sx={{ ml: 0.5, fontSize: '1rem' }} />
                                    </CardAction>
                                </StyledCard>
                            </CardLink>
                        </Grid>
                    ))}
                </Grid>
            </StyledContainer>
        </Root>
    );
};

export default HelpCenter;
